import React, { forwardRef } from 'react';

interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'filled';
  fullWidth?: boolean;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  minRows?: number;
  maxRows?: number;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      size = 'md',
      variant = 'default',
      fullWidth = false,
      resize = 'vertical',
      minRows = 3,
      maxRows,
      className = '',
      style = {},
      ...props
    },
    ref
  ) => {
    const getSizeClasses = (): string => {
      switch (size) {
        case 'sm':
          return 'px-2 py-2 text-sm';
        case 'lg':
          return 'px-4 py-4 text-lg';
        default:
          return 'px-3 py-3 text-base';
      }
    };

    const getVariantStyles = (): React.CSSProperties => {
      const baseStyles: React.CSSProperties = {
        minHeight: `${minRows * 1.5}rem`,
        maxHeight: maxRows ? `${maxRows * 1.5}rem` : undefined,
        resize,
        ...style,
      };

      const focusStyles: React.CSSProperties = {
        '--tw-ring-color': error ? 'var(--error)' : 'var(--accent)',
        '--tw-ring-opacity': '0.2',
      } as React.CSSProperties;

      switch (variant) {
        case 'outlined':
          return {
            ...baseStyles,
            ...focusStyles,
            backgroundColor: 'transparent',
            border: `2px solid ${error ? 'var(--error)' : 'var(--border)'}`,
            color: 'var(--text-primary)',
          };
        case 'filled':
          return {
            ...baseStyles,
            ...focusStyles,
            backgroundColor: 'var(--bg-secondary)',
            border: `1px solid ${error ? 'var(--error)' : 'transparent'}`,
            color: 'var(--text-primary)',
          };
        default:
          return {
            ...baseStyles,
            ...focusStyles,
            backgroundColor: 'var(--bg-primary)',
            border: `1px solid ${error ? 'var(--error)' : 'var(--border)'}`,
            color: 'var(--text-primary)',
          };
      }
    };

    const textareaClasses = [
      'textarea',
      'rounded-lg',
      'transition-colors',
      'focus:outline-none',
      'focus:ring-2',
      getSizeClasses(),
      fullWidth ? 'w-full' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const textareaId =
      props.id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className={`flex flex-col ${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <label
            htmlFor={textareaId}
            className="mb-2 text-sm font-medium"
            style={{ color: 'var(--text-primary)' }}
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={textareaClasses}
          style={getVariantStyles()}
          onFocus={e => {
            const target = e.target as HTMLTextAreaElement;
            target.style.borderColor = error ? 'var(--error)' : 'var(--accent)';
            target.style.boxShadow = `0 0 0 2px ${error ? 'var(--error)' : 'var(--accent)'}20`;
            if (props.onFocus) props.onFocus(e);
          }}
          onBlur={e => {
            const target = e.target as HTMLTextAreaElement;
            target.style.borderColor = error ? 'var(--error)' : 'var(--border)';
            target.style.boxShadow = 'none';
            if (props.onBlur) props.onBlur(e);
          }}
          {...props}
        />
        {(error || helperText) && (
          <div className="mt-1 text-xs">
            {error ? (
              <span style={{ color: 'var(--error)' }}>{error}</span>
            ) : (
              helperText && (
                <span style={{ color: 'var(--text-secondary)' }}>
                  {helperText}
                </span>
              )
            )}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
